/* eslint-disable */
import React, { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SessionContext } from "../../../../context/sessionContext";
import Logger from "../../../../logger";
import Loader from "../../common/loader/loader";
import BannerNotification from "../../common/banner/bannerNotification";
import i18n from '../../../../localization/i18n';
import * as rk from '../../../../localization/resourceKeys';
import Checkbox from "../../common/checkbox/checkbox";
import { StepLayout } from "../../common/steplayout/StepLayout";
import { useMFAVerify } from './useMFAVerify';
import CodeInput from './CodeInput';
import CustomLink from "../../common/link/link";
import { useMyVWQueryParams } from '../../../hooks/useMyVWQueryParams';

interface EnterAppCodeProps {
  onChangeMethod: () => void;
  triggerError?: string | null;
  triggerAccountLocked?: boolean;
  triggerLoading?: boolean;
}

const EnterAppCode: React.FC<EnterAppCodeProps> = ({ onChangeMethod, triggerError, triggerAccountLocked = false, triggerLoading = false }) => {
  const { sessionDetails } = useContext(SessionContext)!;
  const {
    code,
    isError,
    accountLocked,
    failedAttempts,
    submitPending,
    rememberDevice,
    verifyError,
    inputRef,
    handleCodeChange,
    handleVerify,
    setRememberDevice,
  } = useMFAVerify('EnterAppCode');
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const loginAuthenticatorCode = urlParams.get('loginAuthenticatorCode') ?? '';
  const { buildSearch } = useMyVWQueryParams();
  const isLocked = accountLocked || triggerAccountLocked;
  const showTriggerError = !!triggerError && !isLocked;
  const showVerifyError = isError && !isLocked && !showTriggerError;
  const showWarningBanner = failedAttempts >= 2 && !isLocked;
  const isBusy = submitPending || triggerLoading;
  const isInteractionDisabled = isBusy || isLocked;

  return (
    <StepLayout
      title={<h3 className='u-mb-small'>{i18n.t(rk.TITLE_MYVW_MFA_VERIFY)}</h3>}
    >
      <Loader isLoading={isBusy} />
      {isLocked && (
        <div className="u-mt" data-testid="mfa-locked-banner">
          <BannerNotification notificationStyle="error" title={i18n.t(rk.TITLE_LOCKOUT_ERROR)}>
            {i18n.t(rk.TEXT_LOCKOUT_ERROR_MFA) as any}
          </BannerNotification>
        </div>
      )}
      {showTriggerError && (
        <div className="u-mt" data-testid="trigger-error-banner">
          <BannerNotification notificationStyle="error">
            {triggerError as any}
          </BannerNotification>
        </div>
      )}
      {showWarningBanner && (
        <div className="u-mt" data-testid="mfa-warning-banner">
          <BannerNotification
            title={`${i18n.t(rk.TITLE_LOCKOUT_WARNING1)} ${5 - failedAttempts}
             ${failedAttempts === 4 ? i18n.t(rk.TITLE_LOCKOUT_WARNING3) : i18n.t(rk.TITLE_LOCKOUT_WARNING2)}`}
            notificationStyle="warning"
          >
            {i18n.t(rk.TEXT_LOCKOUT_WARNING_VERIFY) as any}
          </BannerNotification>
        </div>
      )}
      <p className="u-mt">{i18n.t(rk.TEXT_ENTER_CODE)}</p>
      <p className="u-mt">
        <span>{i18n.t(rk.TEXT_SETUP_OR_RESET_AUTHENTICATOR)}</span>
        <CustomLink
          value={i18n.t(rk.LINK_ADD_YOUR_ACCOUNT)}
          ariaLabel="Add your account"
          testid="add-your-account"
          fontSize="16px"
          onClick={() => {
            navigate('/myVW/update-account-authenticator' + buildSearch({ loginAuthenticatorCode }), { replace: true });
          }}
        />
      </p>

      <CodeInput value={code} onChange={handleCodeChange} disabled={isInteractionDisabled} inputRef={inputRef} />
      {(showVerifyError) && !isLocked && !showTriggerError && (
        <p
          className="c-error-message"
          style={{ fontSize: "12px", marginLeft: "0px" }}
        >

          {i18n.t(rk.ERROR_INVALID_MFA_CODE)}
        </p>
      )}
      <div className="u-mv-large">
        <Checkbox
          disabled={isInteractionDisabled}
          label={i18n.t(rk.REMEMBER_DEVICE)}
          id="rememberDevice"
          onChange={() => {
            setRememberDevice(prev => {
              Logger.info(
                `${!prev ? 'Checked' : 'Unchecked'} remember this device checkbox`,
                'Location ==> myVWMfa [EnterAppCode]',
                sessionDetails
              );
              return !prev;
            });
          }}
          checked={rememberDevice}
          testid="remember-device-checkbox"
        />
      </div>
      <div className="o-button-container o-button-container--center u-mt-xlarge">
        <button
          className="c-btn c-btn--primary o-button-container__button"
          onClick={handleVerify}
          disabled={code.length < 6 || isInteractionDisabled}
          data-testid="app-mfa-verify"
        >
          <span className="c-btn__text">{i18n.t(rk.BUTTON_VERIFY)}</span>
        </button>
      </div>
      <div className="o-button-container o-button-container--center u-mt-xlarge">
        <button
          className="c-link o-button-container__button--center"
          onClick={onChangeMethod}
          disabled={submitPending}
          data-testid="app-mfa-change-method"
        >
          <span className="c-btn__text">{i18n.t(rk.LINK_CHANGE_VERIFICATION_METHOD)}</span>
        </button>
      </div>
    </StepLayout>
  );
};

export default EnterAppCode;
